/** Projects (/projects): list, create (shared dialog), rename / edit description, repository, and wiki template */

import { useId, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { FolderKanban, Pencil, Plus } from 'lucide-react'
import { updateProjectSchema, type ProjectSummary, type UpdateProjectInput, type WikiTemplateId } from '@agentwiki/shared'
import { useProjects, useUpdateProject } from '../hooks/use-projects'
import { useCurrentProjectSlug } from '../hooks/use-current-project'
import { useAppStore } from '../stores/app-store'
import { useT } from '../i18n'
import { cn } from '../lib/utils'
import { Modal } from '../components/ui/modal'
import { projectPath } from '../components/shell/nav-config'
import { WikiTemplatePicker } from '../components/setup/setup-pickers'
import { badgeClass, buttonPrimaryClass, buttonSecondaryClass, cardClass, errorTextClass, hintClass, iconButtonClass, inputClass, labelClass } from '../components/ui/styles'

export function ProjectsPage() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const openCreateProject = useAppStore((s) => s.setCreateProjectOpen)
  const current = useCurrentProjectSlug()
  const { data: projects, isLoading, isError, refetch } = useProjects()
  const [editing, setEditing] = useState<ProjectSummary | null>(null)
  const numberFormat = new Intl.NumberFormat(locale)
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('projects.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('projects.subtitle')}</p>
        </div>
        <button type="button" onClick={() => openCreateProject(true)} className={buttonPrimaryClass}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('shell.newProject')}
        </button>
      </header>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <div className={errorTextClass} role="alert">
          {t('shell.projectsError')}{' '}
          <button type="button" onClick={() => void refetch()} className="cursor-pointer font-medium underline underline-offset-2">
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {projects?.map((project) => (
            <li key={project.id} className={cn(cardClass, 'flex flex-col p-5', project.slug === current && 'border-accent')}>
              <div className="flex items-start gap-3">
                <FolderKanban className="mt-1 h-5 w-5 shrink-0 text-accent-text" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={projectPath(project.slug)} className="break-words font-display text-lg font-semibold text-fg hover:text-accent-text">
                      {project.name}
                    </Link>
                    {project.isDefault && <span className={badgeClass}>{t('project.overview.defaultProject')}</span>}
                  </div>
                  <p className="font-mono text-xs text-fg-subtle">{project.slug}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(project)}
                  aria-label={t('projects.editNamed', { name: project.name })}
                  title={t('projects.edit')}
                  className={iconButtonClass}
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-3 line-clamp-3 flex-1 whitespace-pre-line break-words text-sm text-fg-muted">
                {project.description || t('project.overview.noDescription')}
              </p>
              <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-subtle">
                <span>{t('projects.sources', { count: numberFormat.format(project.sourceCount) })}</span>
                <span>{t('projects.pages', { count: numberFormat.format(project.pageCount) })}</span>
                <span>{t('project.overview.updated', { date: dateFormat.format(new Date(project.updatedAt)) })}</span>
              </p>
            </li>
          ))}
        </ul>
      )}

      {editing && <EditProjectDialog project={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

export function EditProjectDialog({ project, onClose }: { project: ProjectSummary; onClose: () => void }) {
  const t = useT()
  const updateProject = useUpdateProject()
  const id = useId()
  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description ?? '')
  const [repoUrl, setRepoUrl] = useState(project.repoUrl ?? '')
  const [wikiTemplate, setWikiTemplate] = useState<WikiTemplateId>(project.wikiTemplate ?? 'custom')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    // Cleared optional fields are sent as null so they are removed
    const raw: UpdateProjectInput = { name, description: description.trim() || null, repoUrl: repoUrl.trim() || null, wikiTemplate }
    const parsed = updateProjectSchema.safeParse(raw)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.path[0] === 'name' ? t('project.create.nameRequired') : t('project.create.failed', { message: parsed.error.issues[0]?.message ?? '' }))
      return
    }
    setError(null)
    try {
      const saved = await updateProject.mutateAsync({ ref: project.slug, input: parsed.data })
      toast.success(t('projects.saved', { name: saved.name }))
      onClose()
    } catch (err) {
      setError(t('settings.saveFailed', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  return (
    <Modal title={t('projects.editTitle')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-name`} className={labelClass}>
            {t('project.create.name')}
          </label>
          <input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required className={inputClass} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-description`} className={labelClass}>
            {t('project.create.description')}
          </label>
          <textarea
            id={`${id}-description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            rows={4}
            className={cn(inputClass, 'resize-y')}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-repo`} className={labelClass}>
            {t('project.create.repoUrl')}
          </label>
          <input
            id={`${id}-repo`}
            type="url"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            maxLength={500}
            placeholder="https://github.com/org/repo"
            className={inputClass}
          />
        </div>
        <WikiTemplatePicker value={wikiTemplate} onChange={setWikiTemplate} />
        <p className={hintClass}>{t('projects.slugFixed', { slug: project.slug })}</p>
        {error && (
          <p role="alert" className={errorTextClass}>
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonSecondaryClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={updateProject.isPending} className={buttonPrimaryClass}>
            {updateProject.isPending ? t('settings.saving') : t('settings.save')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
